// src/features/tuition/components/online/SubjectForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  FormHelperText,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { 
  doc, 
  setDoc, 
  collection, 
  addDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL
} from 'firebase/storage';
import { db, storage } from '../../../../firebase/config';
import { normalizeSubjectDocument } from '../../../../utils/subjectDocument';

const SubjectForm = ({ open, onClose, initialData = null, userId }) => {
  const [formData, setFormData] = useState({
    name: '',
    grade: '',
    description: '',
    topics: 0,
    department: '',
    image: null
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        grade: initialData.grade || '',
        description: initialData.description || '',
        topics: initialData.topics || 0,
        department: initialData.department || '',
        image: initialData.image || null
      });
      
      if (initialData.image) {
        setImagePreview(initialData.image);
      }
    } else {
      resetForm();
    }
  }, [initialData, open]);
  
  const resetForm = () => {
    setFormData({
      name: '',
      grade: '',
      description: '',
      topics: 0,
      department: '',
      image: null
    });
    setImageFile(null);
    setImagePreview('');
    setErrors({});
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          image: 'Please upload a JPEG or PNG image'
        }));
        return;
      }
      
      if (file.size > maxSize) {
        setErrors(prev => ({
          ...prev,
          image: 'Image must be less than 5MB'
        }));
        return;
      }
      
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Clear error
      setErrors(prev => ({
        ...prev,
        image: ''
      }));
    }
  };
  
  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData(prev => ({
      ...prev,
      image: null
    }));
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Subject name is required';
    }
    
    if (!formData.grade) {
      newErrors.grade = 'Grade is required';
    }
    
    if (!formData.department) {
      newErrors.department = 'Department is required';
    }
    
    if (!imageFile && !formData.image) {
      newErrors.image = 'Subject image is required';
    }
    
    setErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };
  
  const uploadImage = async () => {
    if (!imageFile) return formData.image;
    
    const storageRef = ref(storage, `subjects/${userId}/${imageFile.name}_${Date.now()}`);
    await uploadBytes(storageRef, imageFile);
    const imageUrl = await getDownloadURL(storageRef);
    
    return imageUrl;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Upload image if new one is selected
      const imageUrl = await uploadImage();
      
      const subjectData = normalizeSubjectDocument({
        name: formData.name,
        grade: formData.grade,
        description: formData.description,
        topics: parseInt(formData.topics) || 0,
        department: formData.department,
        image: imageUrl,
        completed: 0,
        progress: 0
      }, { userId });
      
      if (initialData && initialData.id) {
        // Update existing subject
        const subjectRef = doc(db, "subjects", initialData.id);
        await setDoc(subjectRef, {
          ...subjectData,
          updatedAt: serverTimestamp()
        }, { merge: true });
        
        setNotification({
          open: true,
          message: 'Subject updated successfully!',
          severity: 'success'
        });
      } else {
        // Create new subject
        const subjectsRef = collection(db, "subjects");
        await addDoc(subjectsRef, {
          ...subjectData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        setNotification({
          open: true,
          message: 'Subject created successfully!',
          severity: 'success'
        });
      }
      
      resetForm();
      
      // Close form after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error("Error saving subject:", error);
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };
  
  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? null : onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ 
          sx: { borderRadius: 2 } 
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              {initialData ? 'Edit Subject' : 'Add New Subject'}
            </Typography>
            <IconButton 
              edge="end" 
              color="inherit" 
              onClick={onClose}
              disabled={loading}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    error={!!errors.name}
                    helperText={errors.name}
                    disabled={loading}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.department} disabled={loading}>
                    <InputLabel>Department</InputLabel>
                    <Select
                      name="department"
                      value={formData.department}
                      label="Department"
                      onChange={handleInputChange}
                      required
                    >
                      <MenuItem value="">Select a department</MenuItem>
                      <MenuItem value="Mathematics">Mathematics</MenuItem>
                      <MenuItem value="Sciences">Sciences</MenuItem>
                      <MenuItem value="Languages">Languages</MenuItem>
                      <MenuItem value="Arts">Arts</MenuItem>
                      <MenuItem value="Social Sciences">Social Sciences</MenuItem>
                      <MenuItem value="Commerce">Commerce</MenuItem>
                      <MenuItem value="Technology">Technology</MenuItem>
                      <MenuItem value="Life Skills">Life Skills</MenuItem>
                    </Select>
                    {errors.department && <FormHelperText>{errors.department}</FormHelperText>}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.grade} disabled={loading}>
                    <InputLabel>Grade</InputLabel>
                    <Select
                      name="grade"
                      value={formData.grade}
                      label="Grade"
                      onChange={handleInputChange}
                      required
                    >
                      <MenuItem value="">Select a grade</MenuItem>
                      <MenuItem value="R">Grade R</MenuItem>
                      <MenuItem value="1">Grade 1</MenuItem>
                      <MenuItem value="2">Grade 2</MenuItem>
                      <MenuItem value="3">Grade 3</MenuItem>
                      <MenuItem value="4">Grade 4</MenuItem>
                      <MenuItem value="5">Grade 5</MenuItem>
                      <MenuItem value="6">Grade 6</MenuItem>
                      <MenuItem value="7">Grade 7</MenuItem>
                      <MenuItem value="8">Grade 8</MenuItem>
                      <MenuItem value="9">Grade 9</MenuItem>
                      <MenuItem value="10">Grade 10</MenuItem>
                      <MenuItem value="11">Grade 11</MenuItem>
                      <MenuItem value="12">Grade 12</MenuItem>
                    </Select>
                    {errors.grade && <FormHelperText>{errors.grade}</FormHelperText>}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    multiline
                    rows={4}
                    disabled={loading}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Number of Topics"
                    name="topics"
                    type="number"
                    value={formData.topics}
                    onChange={handleInputChange}
                    inputProps={{ min: 0 }}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" component="div" gutterBottom>
                  Subject Image
                </Typography>
                <Typography variant="body2" component="div" color="text.secondary" paragraph>
                  Upload an image to represent this subject. Recommended size: 800 x 500 pixels.
                </Typography>
              </Box>
              
              {imagePreview ? (
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <img
                    src={imagePreview}
                    alt="Subject preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.7)',
                      }
                    }}
                    onClick={removeImage}
                    disabled={loading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: '2px dashed #e0e0e0',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    bgcolor: 'rgba(0,0,0,0.02)',
                    mb: 2
                  }}
                >
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="subject-image-upload"
                    type="file"
                    onChange={handleImageChange}
                    disabled={loading}
                  />
                  <label htmlFor="subject-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      sx={{
                        borderColor: THEME_COLORS.primary,
                        color: THEME_COLORS.primary,
                        '&:hover': {
                          borderColor: THEME_COLORS.primary,
                          bgcolor: 'rgba(39, 35, 97, 0.04)'
                        }
                      }}
                      disabled={loading}
                    >
                      Upload Image
                    </Button>
                  </label>
                  <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 1 }}>
                    JPEG or PNG, up to 5MB
                  </Typography>
                </Box>
              )}
              
              {errors.image && (
                <Typography variant="body2" component="div" color="error" sx={{ mt: 1 }}>
                  {errors.image}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={onClose} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': { bgcolor: '#1e1c4e' }
            }}
          >
            {loading ? 'Saving...' : initialData ? 'Update Subject' : 'Add Subject'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SubjectForm;
